import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  ButtonGroup,
  Paper,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import CodeIcon from '@mui/icons-material/Code';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

interface HtmlEmailEditorProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
}

const HtmlEmailEditor: React.FC<HtmlEmailEditorProps> = ({ value, onChange, error, helperText }) => {
  const [editorMode, setEditorMode] = useState<'visual' | 'code'>('visual');
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [importHtml, setImportHtml] = useState('');

  // Templates básicos de email
  const emailTemplates = [
    {
      name: 'Email Simples',
      html: `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Email</title>
</head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
    <div style="background-color: #f8f9fa; padding: 30px; border-radius: 10px;">
        <h1 style="color: #2c3e50; margin-bottom: 20px;">Olá {{first_name}}!</h1>
        
        <p style="font-size: 16px; margin-bottom: 15px;">
            Escreva seu conteúdo aqui...
        </p>
        
        <a href="#" style="display: inline-block; background-color: #3498db; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; margin-top: 20px;">
            Botão de Ação
        </a>
        
        <hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;">
        
        <p style="font-size: 14px; color: #7f8c8d; text-align: center;">
            © 2026 Advogada Parceira. Todos os direitos reservados.
        </p>
    </div>
</body>
</html>`
    },
    {
      name: 'Anúncio de Novidades',
      html: `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="font-family: Arial, sans-serif; margin: 0; padding: 0; background-color: #f4f4f4;">
    <table width="100%" cellpadding="0" cellspacing="0" border="0">
        <tr>
            <td align="center" style="padding: 20px;">
                <table width="600" cellpadding="0" cellspacing="0" border="0" style="background-color: white; border-radius: 10px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                    <!-- Header -->
                    <tr>
                        <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px; text-align: center;">
                            <h1 style="color: white; margin: 0; font-size: 28px;">🎉 Novidades!</h1>
                        </td>
                    </tr>
                    
                    <!-- Content -->
                    <tr>
                        <td style="padding: 40px;">
                            <h2 style="color: #333; margin-top: 0;">Olá {{first_name}},</h2>
                            
                            <p style="font-size: 16px; line-height: 1.6; color: #555;">
                                Temos grandes novidades para você! Confira as últimas atualizações da plataforma:
                            </p>
                            
                            <ul style="font-size: 16px; line-height: 1.8; color: #555;">
                                <li>✨ Nova funcionalidade 1</li>
                                <li>🚀 Nova funcionalidade 2</li>
                                <li>💎 Nova funcionalidade 3</li>
                            </ul>
                            
                            <div style="text-align: center; margin: 30px 0;">
                                <a href="#" style="display: inline-block; background-color: #667eea; color: white; padding: 15px 40px; text-decoration: none; border-radius: 50px; font-weight: bold;">
                                    Saiba Mais
                                </a>
                            </div>
                        </td>
                    </tr>
                    
                    <!-- Footer -->
                    <tr>
                        <td style="background-color: #f8f9fa; padding: 20px; text-align: center; color: #999; font-size: 14px;">
                            <p style="margin: 0;">Advogada Parceira - Inteligência Artificial Jurídica</p>
                            <p style="margin: 5px 0 0 0;">© 2026 Todos os direitos reservados</p>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`
    },
    {
      name: 'Promoção/Upgrade',
      html: `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="font-family: Arial, sans-serif; margin: 0; padding: 0; background-color: #f0f0f0;">
    <table width="100%" cellpadding="0" cellspacing="0" border="0">
        <tr>
            <td align="center" style="padding: 20px;">
                <table width="600" cellpadding="0" cellspacing="0" border="0" style="background-color: white;">
                    <!-- Banner Promo -->
                    <tr>
                        <td style="background: linear-gradient(45deg, #FF6B6B, #FFE66D); padding: 10px; text-align: center;">
                            <h3 style="color: white; margin: 0; font-size: 18px; text-shadow: 2px 2px 4px rgba(0,0,0,0.3);">
                                🔥 OFERTA ESPECIAL - 30% OFF
                            </h3>
                        </td>
                    </tr>
                    
                    <!-- Content -->
                    <tr>
                        <td style="padding: 40px;">
                            <h1 style="color: #333; margin-top: 0;">Olá {{first_name}}!</h1>
                            
                            <p style="font-size: 18px; line-height: 1.6; color: #555;">
                                Aproveite nossa <strong>promoção exclusiva</strong> para fazer upgrade do seu plano!
                            </p>
                            
                            <div style="background-color: #f8f9fa; border-left: 4px solid #FF6B6B; padding: 20px; margin: 20px 0;">
                                <h2 style="margin-top: 0; color: #FF6B6B;">Benefícios do Upgrade:</h2>
                                <ul style="line-height: 1.8; color: #555;">
                                    <li>✅ Mais créditos mensais</li>
                                    <li>✅ Acesso a templates premium</li>
                                    <li>✅ Suporte prioritário</li>
                                    <li>✅ Recursos exclusivos</li>
                                </ul>
                            </div>
                            
                            <div style="text-align: center; margin: 30px 0;">
                                <a href="#" style="display: inline-block; background: linear-gradient(45deg, #FF6B6B, #FF8E53); color: white; padding: 18px 50px; text-decoration: none; border-radius: 50px; font-weight: bold; font-size: 18px; box-shadow: 0 4px 15px rgba(255,107,107,0.4);">
                                    Fazer Upgrade Agora
                                </a>
                            </div>
                            
                            <p style="text-align: center; color: #999; font-size: 14px; margin-top: 20px;">
                                ⏰ Oferta válida por tempo limitado
                            </p>
                        </td>
                    </tr>
                    
                    <!-- Footer -->
                    <tr>
                        <td style="background-color: #333; padding: 20px; text-align: center; color: white; font-size: 14px;">
                            <p style="margin: 0;">Advogada Parceira</p>
                            <p style="margin: 10px 0 0 0; color: #999;">
                                Se não deseja receber estes emails, 
                                <a href="#" style="color: #FFE66D;">clique aqui</a>
                            </p>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`
    }
  ];

  const handleImportFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setImportHtml(content);
        setOpenImportDialog(true);
      };
      reader.readAsText(file);
    }
  };

  const handleConfirmImport = () => {
    onChange(importHtml);
    setOpenImportDialog(false);
    setImportHtml('');
  };

  const handleSelectTemplate = (templateHtml: string) => {
    onChange(templateHtml);
    setOpenTemplateDialog(false);
  };

  const insertAtCursor = (before: string, after: string = '') => {
    const textarea = document.getElementById('html-content') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = value;
    const selectedText = text.substring(start, end);
    
    const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
    onChange(newText);

    // Restaurar foco e seleção
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + before.length + selectedText.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const formatButtons = [
    {
      icon: <FormatBoldIcon />,
      tooltip: 'Negrito',
      action: () => insertAtCursor('<strong>', '</strong>')
    },
    {
      icon: <FormatItalicIcon />,
      tooltip: 'Itálico',
      action: () => insertAtCursor('<em>', '</em>')
    },
    {
      icon: <FormatUnderlinedIcon />,
      tooltip: 'Sublinhado',
      action: () => insertAtCursor('<u>', '</u>')
    },
    {
      icon: <LinkIcon />,
      tooltip: 'Link',
      action: () => insertAtCursor('<a href="URL">', '</a>')
    },
    {
      icon: <ImageIcon />,
      tooltip: 'Imagem',
      action: () => insertAtCursor('<img src="URL" alt="descrição" style="max-width: 100%;">')
    },
    {
      icon: <FormatListBulletedIcon />,
      tooltip: 'Lista',
      action: () => insertAtCursor('<ul>\n  <li>', '</li>\n</ul>')
    },
    {
      icon: <FormatListNumberedIcon />,
      tooltip: 'Lista Numerada',
      action: () => insertAtCursor('<ol>\n  <li>', '</li>\n</ol>')
    }
  ];

  return (
    <Box>
      <Paper variant="outlined" sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={editorMode} onChange={(_, newValue) => setEditorMode(newValue)}>
            <Tab label="Visual" value="visual" />
            <Tab label="Código HTML" value="code" icon={<CodeIcon />} iconPosition="start" />
          </Tabs>
          
          <Box>
            <Button
              size="small"
              startIcon={<InsertDriveFileIcon />}
              onClick={() => setOpenTemplateDialog(true)}
              sx={{ mr: 1 }}
            >
              Templates
            </Button>
            <Button
              size="small"
              component="label"
              startIcon={<UploadFileIcon />}
              variant="outlined"
            >
              Importar HTML
              <input
                type="file"
                hidden
                accept=".html,.htm"
                onChange={handleImportFile}
              />
            </Button>
          </Box>
        </Box>

        {editorMode === 'visual' && (
          <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
            <ButtonGroup size="small" variant="outlined">
              {formatButtons.map((btn, index) => (
                <Tooltip key={index} title={btn.tooltip}>
                  <IconButton size="small" onClick={btn.action}>
                    {btn.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </ButtonGroup>
            <Alert severity="info" sx={{ mt: 1, py: 0 }}>
              <Typography variant="caption">
                Use <strong>{'{{first_name}}'}</strong>, <strong>{'{{last_name}}'}</strong>, <strong>{'{{full_name}}'}</strong> para personalização
              </Typography>
            </Alert>
          </Box>
        )}

        <TextField
          id="html-content"
          fullWidth
          multiline
          rows={editorMode === 'code' ? 20 : 15}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          error={error}
          helperText={helperText}
          placeholder={editorMode === 'code' 
            ? 'Cole seu código HTML aqui ou use os templates...' 
            : 'Escreva o conteúdo do email...'
          }
          sx={{
            '& .MuiInputBase-root': {
              fontFamily: editorMode === 'code' ? 'monospace' : 'inherit',
              fontSize: editorMode === 'code' ? '13px' : 'inherit'
            },
            '& fieldset': { border: 'none' }
          }}
        />
      </Paper>

      {/* Preview */}
      {value && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Prévia:
          </Typography>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              maxHeight: 400, 
              overflow: 'auto',
              bgcolor: 'grey.50'
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: value }} />
          </Paper>
        </Box>
      )}

      {/* Dialog de Templates */}
      <Dialog open={openTemplateDialog} onClose={() => setOpenTemplateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Escolher Template</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {emailTemplates.map((template, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 2
                    }
                  }}
                  onClick={() => handleSelectTemplate(template.html)}
                >
                  <Typography variant="h6" gutterBottom>
                    {template.name}
                  </Typography>
                  <Box
                    sx={{
                      height: 200,
                      overflow: 'hidden',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      '& *': {
                        fontSize: '6px !important',
                        padding: '1px !important',
                        margin: '0 !important'
                      }
                    }}
                    dangerouslySetInnerHTML={{ __html: template.html }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTemplateDialog(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Importação */}
      <Dialog open={openImportDialog} onClose={() => setOpenImportDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Confirmar Importação</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Isso substituirá o conteúdo atual. Tem certeza?
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={15}
            value={importHtml}
            onChange={(e) => setImportHtml(e.target.value)}
            sx={{ fontFamily: 'monospace', fontSize: '13px' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImportDialog(false)}>Cancelar</Button>
          <Button onClick={handleConfirmImport} variant="contained" color="primary">
            Confirmar Importação
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HtmlEmailEditor;



